import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import { ComponentProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HorizontalScrollContext } from '../../context/HorizontalScrollContextProvider';
import { TableDataGroup } from '../types';
import { TableRow } from './TableRow';

const GroupHeader = ({
  row,
  setCollapsed,
  isCollapsed,
}: {
  row: TableDataGroup;
  setCollapsed: (collapsed: boolean | ((c: boolean) => boolean)) => void;
  isCollapsed: boolean;
}) => {
  const { horizontalScroll } = useContext(HorizontalScrollContext);
  const { t } = useTranslation();

  return (
    <tr>
      <CellWithHierarchy
        hierarchyProps={{
          level: row.accumulatedRow.level as ComponentProps<typeof CellWithHierarchy>['hierarchyProps']['level'],
          onToggleCollapsed: () => setCollapsed(c => !c),
          collapseButtonLabelText: t('PAGES.RESULTS.PER_STUDENT.TABLE.TOC_NODE.COLLAPSE_BUTTON.A11Y_LABEL'),
          collapsed: isCollapsed,
        }}
        highlighted
        density="normal"
        sticky={horizontalScroll}
      >
        <Cell.Text
          title={row.accumulatedRow.title}
          prominent
          subTitleTop={row.accumulatedRow.subTitle as string}
          startAdornment={
            row.accumulatedRow.image ? (
              <Avatar>
                <AvatarImage
                  size="sm"
                  name={t('PAGES.RESULTS.PER_STUDENT.TABLE.TOC_NODE.AVATAR.A11Y_LABEL')}
                  src={row.accumulatedRow.image}
                />
              </Avatar>
            ) : undefined
          }
        />
      </CellWithHierarchy>
      {row.accumulatedRow.columns.map((column, i) => {
        if (column && 'Cell' in column) {
          return column.Cell();
        }

        return column && 'score' in column && column.score ? (
          <Cell key={column.id} highlighted>
            <Cell.Results
              awaiting={column.pending}
              success={column.score.achieved}
              failure={column.score.achievable - column.score.achieved}
              undone={column.score.maximum - (column.score.achievable + column.pending)}
              quantifiable
              variant="bar"
            />
          </Cell>
        ) : (
          <Cell key={i} highlighted />
        );
      })}
    </tr>
  );
};

const TableGroup = ({ row }: { row: TableDataGroup }) => {
  const [isCollapsed, setCollapsed] = useState(false);

  return (
    <>
      <GroupHeader isCollapsed={isCollapsed} setCollapsed={setCollapsed} row={row} />
      {!isCollapsed && row.rows.map(row => <TableRow key={`table-row-${row.id}`} row={row} />)}
    </>
  );
};

export default TableGroup;
